import type NumberUnit from '@exodus/currency';
import { formatBalance } from '@exodus/formatting/lib/asset';
import Button from '@passkeys/ui/button';
import Text from '@passkeys/ui/text';
import assert from 'minimalistic-assert';

import { actions } from '@/ui/flux';
import useAssets from '@/ui/hooks/use-assets';
import useNavigate from '@/ui/hooks/use-navigate';
import type { ReferrerLocationState } from '@/ui/utils/navigation';
import { createNavigateStateOptions } from '@/ui/utils/navigation';
import Header from './components/header.js';

export type SwapSuccessModalAction = {
  label: string;
  route: string;
};

type ShowSwapSuccessModalOptions = {
  toAmount: NumberUnit;
  fromAmount?: NumberUnit;
  toAssetName: string;
  fromAssetName?: string;
  primaryAction: SwapSuccessModalAction;
  alternativeAction: SwapSuccessModalAction;
  onClose?: () => void;
};

export const SWAP_SUCCESS_MODAL_TITLE = 'Success';

const SwapSuccessModal = ({
  toAmount,
  fromAmount,
  toAssetName,
  fromAssetName,
  primaryAction,
  alternativeAction,
  onClose,
}: ShowSwapSuccessModalOptions) => {
  const navigate = useNavigate();

  const assets = useAssets();
  const toAsset = toAssetName ? assets[toAssetName] : undefined;
  assert(toAsset, `Invalid to-asset ${toAssetName}`);
  const fromAsset = fromAssetName ? assets[fromAssetName] : undefined;
  assert(!fromAssetName || fromAsset, `Invalid from-asset ${fromAssetName}`);

  const navigateAndClose = (route: string) => {
    navigate(
      route,
      createNavigateStateOptions<ReferrerLocationState>({
        referrer: 'swap-success',
      }),
    );

    onClose?.();
  };

  return (
    <div className="flex h-full flex-col pb-8">
      <div className="flex w-full flex-1 flex-col items-center justify-center gap-y-6 pb-6">
        <Header />

        <Text size={21} align="center" className="leading-none">
          Swap successful
        </Text>

        <div className="flex flex-col items-center gap-y-2">
          <Text size={24}>{formatBalance(toAmount, toAsset)}</Text>

          {fromAsset && fromAmount && (
            <Text
              monospace
              className="text-neutral-100 theme-dark:text-neutral-300"
            >
              from {formatBalance(fromAmount, fromAsset)}
            </Text>
          )}
        </div>
      </div>
      <div className="flex w-full flex-col items-center">
        <Button
          className="w-full max-w-[296px]"
          size="large"
          onClick={() => navigateAndClose(primaryAction.route)}
          dataTestId="success-primary-button"
        >
          {primaryAction.label}
        </Button>
        <Button
          className="-mb-2 mt-2"
          variant="tertiary"
          dataTestId="success-tertiary-button"
          onClick={() => navigateAndClose(alternativeAction.route)}
        >
          {alternativeAction.label}
        </Button>
      </div>
    </div>
  );
};

const showSwapSuccessModal = async ({
  toAmount,
  fromAmount,
  toAssetName,
  fromAssetName,
  primaryAction,
  alternativeAction,
  onClose,
}: ShowSwapSuccessModalOptions) =>
  actions.modal.show({
    canClose: false,
    fullscreen: true,
    title: SWAP_SUCCESS_MODAL_TITLE,
    onClose,
    reactContent: (
      <SwapSuccessModal
        toAmount={toAmount}
        fromAmount={fromAmount}
        toAssetName={toAssetName}
        fromAssetName={fromAssetName}
        primaryAction={primaryAction}
        alternativeAction={alternativeAction}
      />
    ),
  });

export default showSwapSuccessModal;
